import React, { useState } from 'react';
import { callsApi } from '../services/api';
import PCADashboard from './PCADashboard';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Paper,
  Tooltip,
  Badge,
  LinearProgress
} from '@mui/material';
import {
  Close,
  Phone,
  Email,
  Schedule,
  Edit,
  Add,
  History,
  Notes,
  Star,
  Warning,
  CheckCircle,
  Person,
  Business,
  LocationOn,
  AccessTime,
  TrendingUp,
  TrendingDown,
  RecordVoiceOver,
  PlayArrow,
  Assessment
} from '@mui/icons-material';

interface Customer {
  id: string;
  name: string;
  phone: string;
  email?: string;
  issueCategory: string;
  issueDetails: string;
  urgencyLevel: 'Low' | 'Medium' | 'High' | 'Critical';
  status: 'New' | 'In Progress' | 'Resolved' | 'Follow-up Required';
  aiConfidence: number;
  lastContact: string;
  callCount: number;
  priority: 'Low' | 'Medium' | 'High' | 'Critical';
  transcript?: string;
  recordingUrl?: string;
  recordingPath?: string;
  callId?: string;
  duration?: number;
  troubleshootingSteps?: string;
  resolutionStatus?: string;
}

interface CustomerDetailModalProps {
  open: boolean;
  customer: Customer | null;
  onClose: () => void;
  onUpdate: (customer: Customer) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customer-tabpanel-${index}`}
      aria-labelledby={`customer-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const CustomerDetailModal: React.FC<CustomerDetailModalProps> = ({
  open,
  customer,
  onClose,
  onUpdate
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editedCustomer, setEditedCustomer] = useState<Customer | null>(null);
  const [callDetails, setCallDetails] = useState<any>(null);
  const [loadingCallDetails, setLoadingCallDetails] = useState(false);

  const fetchCallDetails = React.useCallback(async (callId: string) => {
    try {
      setLoadingCallDetails(true);
      const response = await callsApi.getCallById(callId);
      setCallDetails(response.data);
      // Update editedCustomer with transcript data if available
      if (response.data.transcript || response.data.recording_url) {
        setEditedCustomer(prev => prev ? {
          ...prev,
          transcript: response.data.transcript,
          recordingUrl: response.data.recording_url,
          recordingPath: response.data.recording_path,
          duration: response.data.duration || response.data.duration_seconds,
          troubleshootingSteps: response.data.troubleshooting_steps,
          resolutionStatus: response.data.resolution_status
        } : null);
      }
    } catch (error) {
      console.error('Error fetching call details:', error);
    } finally {
      setLoadingCallDetails(false);
    }
  }, []);

  React.useEffect(() => {
    if (customer) {
      setEditedCustomer({ ...customer });
      // Fetch full call details including transcript if we have a call ID
      if (customer.callId || customer.id) {
        fetchCallDetails(customer.callId || customer.id);
      }
    }
  }, [customer, fetchCallDetails]);


  const formatTranscript = (transcript: string): JSX.Element[] => {
    if (!transcript) return [<Typography key="none" color="text.secondary">No transcript available</Typography>];
    
    const lines = transcript.split('\n').filter(line => line.trim());
    return lines.map((line, index) => {
      if (line.startsWith('Agent:')) {
        return (
          <Box key={index} sx={{ mb: 1, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
              Nova (AI):
            </Typography>
            <Typography variant="body2" sx={{ color: '#080707', ml: 2 }}>
              {line.substring(7).trim()}
            </Typography>
          </Box>
        );
      } else if (line.includes(':') && !line.startsWith('Agent:')) {
        const colonIndex = line.indexOf(':');
        const speaker = line.substring(0, colonIndex).trim();
        const message = line.substring(colonIndex + 1).trim();
        
        if (speaker.toLowerCase().includes('customer') || speaker.toLowerCase().includes('caller')) {
          return (
            <Box key={index} sx={{ mb: 1, pl: 2, borderLeft: '3px solid', borderColor: 'info.main' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.main', mb: 0.5 }}>
                {speaker}:
              </Typography>
              <Typography variant="body2" sx={{ color: '#080707', ml: 2 }}>
                {message}
              </Typography>
            </Box>
          );
        }
      }
      
      return (
        <Typography key={index} variant="body2" sx={{ mb: 0.5, color: '#706E6B', ml: 2 }}>
          {line.trim()}
        </Typography>
      );
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    if (editedCustomer) {
      onUpdate(editedCustomer);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditedCustomer(customer ? { ...customer } : null);
    setIsEditing(false);
  };

  const handleFieldChange = (field: keyof Customer, value: any) => {
    if (editedCustomer) {
      setEditedCustomer({ ...editedCustomer, [field]: value });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'New': return 'primary';
      case 'In Progress': return 'warning';
      case 'Resolved': return 'success';
      case 'Follow-up Required': return 'error';
      default: return 'default';
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'Low': return 'success';
      case 'Medium': return 'warning';
      case 'High': return 'error';
      case 'Critical': return 'error';
      default: return 'default';
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  // Get contact history from customer's calls
  const contactHistory = customer?.callId ? [{
    id: customer.callId,
    type: 'call' as const,
    timestamp: customer.lastContact || new Date().toISOString(),
    duration: customer.duration ? `${Math.floor(customer.duration / 60)}:${String(customer.duration % 60).padStart(2, '0')}` : 'N/A',
    outcome: customer.resolutionStatus || (customer.status === 'Resolved' ? 'Resolved' : 'In Progress'),
    notes: customer.issueDetails || customer.troubleshootingSteps || 'No notes available',
    agent: 'AI Assistant'
  }] : [];

  // Get service notes from troubleshooting steps and issue details
  const serviceNotes = customer?.troubleshootingSteps ? [{
    id: customer.callId || '1',
    timestamp: customer.lastContact || new Date().toISOString(),
    note: customer.troubleshootingSteps,
    author: 'AI Assistant',
    type: 'technical' as const
  }] : [];

  if (!customer || !editedCustomer) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ width: 48, height: 48 }}>
              {getInitials(customer.name)}
            </Avatar>
            <Box>
              <Typography variant="h6">{customer.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                Customer ID: {customer.id}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <IconButton onClick={handleEdit} size="small">
              <Edit />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Overview" />
            <Tab label="Contact History" />
            <Tab label="Service Notes" />
            <Tab label="Post-Call Analysis" icon={<Assessment />} iconPosition="start" />
            <Tab label="Call Transcripts & Recordings" icon={<RecordVoiceOver />} iconPosition="start" />
            <Tab label="Follow-up" />
          </Tabs>
        </Box>

        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Customer Info */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Customer Information
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Full Name
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={editedCustomer.name}
                          onChange={(e) => handleFieldChange('name', e.target.value)}
                        />
                      ) : (
                        <Typography variant="body1">{customer.name}</Typography>
                      )}
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Phone Number
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={editedCustomer.phone}
                          onChange={(e) => handleFieldChange('phone', e.target.value)}
                        />
                      ) : (
                        <Typography variant="body1">{customer.phone}</Typography>
                      )}
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Email Address
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={editedCustomer.email || ''}
                          onChange={(e) => handleFieldChange('email', e.target.value)}
                        />
                      ) : (
                        <Typography variant="body1">{customer.email || 'Not provided'}</Typography>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Status & Priority */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Status & Priority
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Current Status
                      </Typography>
                      {isEditing ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={editedCustomer.status}
                            onChange={(e) => handleFieldChange('status', e.target.value)}
                          >
                            <MenuItem value="New">New</MenuItem>
                            <MenuItem value="In Progress">In Progress</MenuItem>
                            <MenuItem value="Resolved">Resolved</MenuItem>
                            <MenuItem value="Follow-up Required">Follow-up Required</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Chip
                          label={customer.status}
                          color={getStatusColor(customer.status) as any}
                          size="small"
                        />
                      )}
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Urgency Level
                      </Typography>
                      {isEditing ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={editedCustomer.urgencyLevel}
                            onChange={(e) => handleFieldChange('urgencyLevel', e.target.value)}
                          >
                            <MenuItem value="Low">Low</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                            <MenuItem value="Critical">Critical</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Chip
                          label={customer.urgencyLevel}
                          color={getUrgencyColor(customer.urgencyLevel) as any}
                          size="small"
                        />
                      )}
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Issue Category
                      </Typography>
                      <Typography variant="body1">{customer.issueCategory}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Performance Metrics */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Performance Metrics
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box textAlign="center">
                        <Typography variant="h4" color="primary">
                          {customer.callCount}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Calls
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box textAlign="center">
                        <Typography variant="h4" color="success.main">
                          {(customer.aiConfidence * 100).toFixed(1)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          AI Confidence
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box textAlign="center">
                        <Typography variant="h4" color="info.main">
                          {new Date(customer.lastContact).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last Contact
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Contact History Tab */}
        <TabPanel value={tabValue} index={1}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Contact History
              </Typography>
              <List>
                {contactHistory.length === 0 ? (
                  <ListItem>
                    <ListItemText
                      primary="No contact history available"
                      secondary="Contact history will appear here once calls are made"
                    />
                  </ListItem>
                ) : (
                  contactHistory.map((contact) => (
                  <ListItem key={contact.id} divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <Phone />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body1">
                            {contact.type === 'call' ? 'Phone Call' : 'Callback'}
                          </Typography>
                          <Chip
                            label={contact.outcome}
                            color={contact.outcome === 'Resolved' ? 'success' : 'warning'}
                            size="small"
                          />
                        </Stack>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(contact.timestamp).toLocaleString()} • Duration: {contact.duration}
                          </Typography>
                          <Typography variant="body2">
                            {contact.notes}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Agent: {contact.agent}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  ))
                )}
              </List>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Service Notes Tab */}
        <TabPanel value={tabValue} index={2}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">
                  Service Notes
                </Typography>
                <Button startIcon={<Add />} variant="outlined" size="small">
                  Add Note
                </Button>
              </Stack>
              <List>
                {serviceNotes.length === 0 ? (
                  <ListItem>
                    <ListItemText
                      primary="No service notes available"
                      secondary="Service notes will appear here once calls are analyzed"
                    />
                  </ListItem>
                ) : (
                  serviceNotes.map((note) => (
                  <ListItem key={note.id} divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'secondary.main' }}>
                        <Notes />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body1">
                            {note.type === 'technical' ? 'Technical Note' : 
                             note.type === 'sales' ? 'Sales Note' : 'General Note'}
                          </Typography>
                          <Chip
                            label={note.type}
                            color="primary"
                            size="small"
                          />
                        </Stack>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {new Date(note.timestamp).toLocaleString()} • {note.author}
                          </Typography>
                          <Typography variant="body2">
                            {note.note}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  ))
                )}
              </List>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Post-Call Analysis Tab */}
        <TabPanel value={tabValue} index={3}>
          <PCADashboard
            pcaData={{
              customerName: editedCustomer?.name || customer?.name,
              issueCategory: editedCustomer?.issueCategory,
              issueDetails: editedCustomer?.issueDetails,
              troubleshootingSteps: editedCustomer?.troubleshootingSteps || callDetails?.troubleshooting_steps,
              resolutionStatus: editedCustomer?.resolutionStatus || callDetails?.resolution_status,
              urgencyLevel: editedCustomer?.urgencyLevel,
              aiConfidence: editedCustomer?.aiConfidence || callDetails?.ai_confidence,
              callId: editedCustomer?.callId || customer?.callId || callDetails?.call_id,
              duration: editedCustomer?.duration || callDetails?.duration_seconds
            }}
            callMetadata={{
              startTime: callDetails?.start_time,
              endTime: callDetails?.end_time,
              duration: editedCustomer?.duration || callDetails?.duration_seconds
            }}
          />
        </TabPanel>

        {/* Call Transcripts & Recordings Tab */}
        <TabPanel value={tabValue} index={4}>
          {loadingCallDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <LinearProgress sx={{ width: '100%' }} />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Call Recording */}
              {(editedCustomer?.recordingUrl || editedCustomer?.recordingPath || callDetails?.recording_url || callDetails?.recording_path) && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                        <PlayArrow color="primary" />
                        <Typography variant="h6">
                          Call Recording
                        </Typography>
                      </Stack>
                      <Box sx={{ mt: 2 }}>
                        <audio controls style={{ width: '100%' }}>
                          <source 
                            src={
                              editedCustomer?.recordingPath 
                                ? `/recordings/${editedCustomer.recordingPath.split('/').pop()}`
                                : editedCustomer?.recordingUrl 
                                ? editedCustomer.recordingUrl
                                : callDetails?.recording_path
                                ? `/recordings/${callDetails.recording_path.split('/').pop()}`
                                : callDetails?.recording_url || ''
                            } 
                            type="audio/wav" 
                          />
                          Your browser does not support the audio element.
                        </audio>
                        {editedCustomer?.duration && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Duration: {Math.round(editedCustomer.duration / 60)} minutes {editedCustomer.duration % 60} seconds
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Call Transcript */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                      <Notes color="primary" />
                      <Typography variant="h6">
                        Call Transcript
                      </Typography>
                    </Stack>
                    <Paper 
                      sx={{ 
                        p: 3, 
                        maxHeight: '500px', 
                        overflow: 'auto',
                        backgroundColor: '#F9F9F9',
                        border: '1px solid #DDDBDA'
                      }}
                    >
                      {editedCustomer?.transcript || callDetails?.transcript ? (
                        <Box>
                          {formatTranscript(editedCustomer?.transcript || callDetails?.transcript)}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No transcript available for this call.
                        </Typography>
                      )}
                    </Paper>
                    {(editedCustomer?.callId || customer?.callId || callDetails?.call_id) && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Call ID: {editedCustomer?.callId || customer?.callId || callDetails?.call_id}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </TabPanel>

        {/* Follow-up Tab */}
        <TabPanel value={tabValue} index={5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Schedule Follow-up
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Follow-up Date"
                      type="datetime-local"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      fullWidth
                      label="Follow-up Type"
                      select
                      size="small"
                    >
                      <MenuItem value="callback">Callback</MenuItem>
                      <MenuItem value="email">Email Follow-up</MenuItem>
                      <MenuItem value="technical">Technical Check</MenuItem>
                      <MenuItem value="sales">Sales Follow-up</MenuItem>
                    </TextField>
                    <TextField
                      fullWidth
                      label="Notes"
                      multiline
                      rows={3}
                      size="small"
                    />
                    <Button variant="contained" startIcon={<Schedule />}>
                      Schedule Follow-up
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quick Actions
                  </Typography>
                  <Stack spacing={2}>
                    <Button
                      variant="outlined"
                      startIcon={<Phone />}
                      fullWidth
                    >
                      Make Call
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Email />}
                      fullWidth
                    >
                      Send Email
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Schedule />}
                      fullWidth
                    >
                      Schedule Callback
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Star />}
                      fullWidth
                    >
                      Mark as Priority
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        {isEditing ? (
          <Stack direction="row" spacing={1}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">
              Save Changes
            </Button>
          </Stack>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CustomerDetailModal;
